/**
 * How a car chosen by model, without a plate, is named (P25009-491).
 *
 * A model alone is not one car: an XC60 is a 2008–2017 car (D) and a 2017–
 * car (U) with other wheels, and a Polestar engineered XC60 is a third. So
 * the search page's car picker asks for the version too, and the car bar and
 * the recent-search list name it the way the car database does:
 *
 *   Volkswagen ID.4 (E2) 2020–
 *   Volvo XC60 (D) 2008–2017
 *   Volvo XC60 Polestar engineered (U) 2019–
 */

const yearOf = value => {
  const match = String(value ?? '').match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : '';
};

/**
 * "Polestar engineered (U) 2019–" — variant, generation code, year range.
 * An open range ends in a dash: the generation is still made.
 *
 * @param {Object} version
 * @param {string} [version.variant]
 * @param {string} [version.chassisCode]
 * @param {number|string} [version.yearFrom]
 * @param {number|string} [version.yearTo] ignored when `isCurrent`
 * @param {boolean} [version.isCurrent] the range has no end
 * @returns {string}
 */
export const versionLabel = ({ variant, chassisCode, yearFrom, yearTo, isCurrent } = {}) => {
  const from = yearOf(yearFrom);
  const to = isCurrent ? '' : yearOf(yearTo);
  const years = from ? `${from}–${to}` : to;
  return [variant, chassisCode ? `(${chassisCode})` : '', years]
    .map(part => (part ? String(part).trim() : ''))
    .filter(Boolean)
    .join(' ');
};

/* The car object inside a fitment payload ({ data: [car] }), or the car itself. */
const carOf = data => (Array.isArray(data?.data) ? data.data[0] : data?.result || data) || null;

/**
 * "Volkswagen ID.4 (E2) 2020–" for a car payload from the car database
 * (searchCarModel / the plate lookup's resolved car).
 *
 * @param {Object} data `{ data: [car] }`
 * @returns {string} '' when the payload names no car
 */
export const modelCarName = data => {
  const car = carOf(data);
  if (!car) return '';
  const make = car.brand || car.make?.name || '';
  const model =
    car.model?.name || (typeof car.model === 'string' ? car.model : '') || car.series?.name || '';
  const version = versionLabel({
    variant: car.name,
    chassisCode: car.chassis_code,
    yearFrom: car.start_year,
    yearTo: car.end_year,
    isCurrent: !car.end_year,
  });
  return [make, model, version].filter(Boolean).join(' ').trim();
};
